import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import FloatingCart from "@/components/FloatingCart";
import Lightbox from "@/components/Lightbox";
import { api, trackWaClick } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { Fish, Waves, Sun, MoonStar, Ship, Sailboat, Fuel, ArrowLeft, Loader2, CalendarCheck, MessageCircle, Anchor, Calculator, MapPin } from "lucide-react";
import { toast } from "sonner";

const WA_ADMIN = "6281377114000";

const PHOTOS = [
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/qakl1sun_image.png", caption: "Tampak Depan Rumah Rakit", icon: Anchor, big: true },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/67yua0bi_image.png", caption: "Teras Santai View Sungai", icon: Waves },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/6ywxfzj8_image.png", caption: "Area Makan & Mancing", icon: Fish },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/zxmupjso_image.png", caption: "Armada Sampan/Kato", icon: Sailboat },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/rqznqx68_image.png", caption: "Armada SpeedBoat 15PK", icon: Ship },
];

const PACKAGES = [
  { id: "rakit_12", icon: Sun, name: "Paket Rumah Rakit 12 Jam", price: "Rp 450.000", desc: "Pagi–Sore / Malam saja — wisata mancing + healing di atas sungai.", discount: true },
  { id: "rakit_24", icon: MoonStar, name: "Paket Rumah Rakit 24 Jam", price: "Rp 900.000", desc: "Nginap 1 hari 1 malam — mancing sepuasnya + sunrise di sungai.", highlight: true, discount: true },
  { id: "speedboat_12", icon: Ship, name: "Sewa SpeedBoat 15PK 12 Jam", price: "Rp 350.000", desc: "Jelajah spot mancing lebih jauh dengan speedboat 15PK." },
  { id: "speedboat_24", icon: Ship, name: "Sewa SpeedBoat 15PK 24 Jam", price: "Rp 500.000", desc: "Speedboat menemani seharian penuh." },
  { id: "bbm", icon: Fuel, name: "BBM SpeedBoat/Sampan Kato", price: "Rp 15.000", per: "/liter", desc: "Dihitung sesuai pemakaian." },
  { id: "sampan_12", icon: Sailboat, name: "Sewa Sampan/Kato 12 Jam", price: "Rp 100.000", desc: "Santai keliling sungai pakai sampan." },
  { id: "sampan_24", icon: Sailboat, name: "Sewa Sampan/Kato 24 Jam", price: "Rp 150.000", desc: "Sampan/kato seharian penuh." },
];

const PRICE_NUM = { rakit_12: 450000, rakit_24: 900000, speedboat_12: 350000, speedboat_24: 500000, sampan_12: 100000, sampan_24: 150000 };
const BBM_PRICE = 15000;
const PAKET_LABEL = { rakit_12: "Rumah Rakit 12 Jam", rakit_24: "Rumah Rakit 24 Jam" };
const rp = (n) => `Rp ${n.toLocaleString("id-ID")}`;

export default function RumahRakit() {
  const [form, setForm] = useState({ name: "", phone: "", package: "rakit_12", visit_date: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);
  const [lightbox, setLightbox] = useState({ images: [], index: null });
  const [calc, setCalc] = useState({ paket: "rakit_24", speedboat: "none", sampan: "none", bbm: "" });

  const calcPaket = PRICE_NUM[calc.paket];
  const calcDiscount = Math.round(calcPaket * 0.1);
  const calcAddons = (calc.speedboat !== "none" ? PRICE_NUM[calc.speedboat] : 0) + (calc.sampan !== "none" ? PRICE_NUM[calc.sampan] : 0);
  const calcBbm = (parseInt(calc.bbm) || 0) * BBM_PRICE;
  const calcTotal = calcPaket - calcDiscount + calcAddons + calcBbm;

  const applyCalc = () => {
    const parts = [
      `Paket ${PAKET_LABEL[calc.paket]} diskon 10% (-${rp(calcDiscount)})`,
      calc.speedboat !== "none" && `SpeedBoat ${calc.speedboat === "speedboat_12" ? "12" : "24"} Jam (${rp(PRICE_NUM[calc.speedboat])})`,
      calc.sampan !== "none" && `Sampan/Kato ${calc.sampan === "sampan_12" ? "12" : "24"} Jam (${rp(PRICE_NUM[calc.sampan])})`,
      calcBbm > 0 && `BBM ${calc.bbm} liter (${rp(calcBbm)})`,
    ].filter(Boolean);
    setForm((f) => ({ ...f, package: calc.paket, notes: `Estimasi kalkulator: ${parts.join(" + ")}. Total estimasi ${rp(calcTotal)}` }));
    toast.success("Estimasi disalin ke form booking");
    document.getElementById("booking-rakit")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      return toast.error("Lengkapi nama dan nomor HP");
    }
    setLoading(true);
    try {
      await api.post("/rumahrakit/booking", form);
      toast.success("Booking terkirim!", { description: "Admin RUMAH RAKIT akan menghubungi kamu via WhatsApp untuk konfirmasi jadwal." });
      setSent(form);
      setForm({ name: "", phone: "", package: "rakit_12", visit_date: "", notes: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim booking");
    } finally {
      setLoading(false);
    }
  };

  const waMessage = sent
    ? `Halo Admin FAS Group, saya ${sent.name} (${sent.phone}) baru booking RUMAH RAKIT — paket: ${PACKAGES.find((p) => p.id === sent.package)?.name}${sent.visit_date ? `, tanggal: ${sent.visit_date}` : ""}.`
    : "Halo Admin FAS Group, saya mau tanya RUMAH RAKIT...";

  return (
    <div className="min-h-screen paper-bg">
      <Navbar />
      <CartDrawer />
      <FloatingCart />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-4">
        <Link to="/" data-testid="back-to-home-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-700 text-white text-sm font-bold shadow-md shadow-emerald-700/25 hover:bg-emerald-800 hover:shadow-lg hover:-translate-y-0.5 transition">
          <ArrowLeft className="w-4 h-4" /> Kembali Belanja
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-4 relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-700 via-cyan-800 to-teal-950 text-white p-8 md:p-12"
        >
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 25%, rgba(45,212,191,0.4), transparent 40%), radial-gradient(circle at 85% 80%, rgba(14,116,144,0.35), transparent 45%)" }} />
          <div className="relative max-w-2xl">
            <span className="tag-pill bg-teal-300 text-teal-950">
              <Fish className="w-3 h-3" /> RUMAH RAKIT by FAS Group Corp.
            </span>
            <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Mancing + healing di <span className="text-teal-300">rumah terapung.</span>
            </h1>
            <p className="mt-5 text-teal-50/90 text-sm md:text-base leading-relaxed max-w-lg">
              Rumah Rakit adalah rumah terapung untuk wisata mancing & healing di atas sungai. Paket 12 jam mulai Rp 450rb — bisa nginap, sewa speedboat, atau sampan kato.
            </p>
            <a
              href="#booking-rakit"
              data-testid="rakit-booking-cta"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal-400 text-teal-950 font-bold hover:bg-teal-300 transition shadow-lg shadow-teal-400/30"
            >
              <CalendarCheck className="w-4 h-4" /> Booking Sekarang
            </a>
          </div>
        </motion.div>
      </section>

      {/* Galeri */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-stone-900">Galeri Rumah Rakit</h2>
        <p className="text-stone-600 mt-1">Klik foto untuk melihat ukuran penuh.</p>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {PHOTOS.map((p, i) => (
            <motion.button
              key={i}
              type="button"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              onClick={() => setLightbox({ images: PHOTOS, index: i })}
              className={`group relative rounded-3xl overflow-hidden border border-[#E5DECF] cursor-zoom-in text-left focus:outline-none focus:ring-2 focus:ring-teal-500 ${p.big ? "col-span-2 row-span-2 md:row-span-1" : ""}`}
              data-testid={`rakit-photo-${i}`}
            >
              <img src={p.url} alt={p.caption} className={`w-full object-cover group-hover:scale-105 transition-transform duration-500 ${p.big ? "h-56 md:h-64" : "h-40 md:h-64"}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/75 via-transparent to-transparent" />
              <div className="absolute bottom-2.5 left-3 right-3 flex items-center gap-1.5">
                <p.icon className="w-3.5 h-3.5 text-teal-300 shrink-0" />
                <span className="font-display font-bold text-white text-xs md:text-sm leading-tight">{p.caption}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Lokasi */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="rakit-location-section">
        <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-stone-900">Lokasi Rumah Rakit 01</h2>
        <p className="text-stone-600 mt-1 flex items-center gap-1.5">
          <MapPin className="w-4 h-4 text-teal-700" /> Dusun/Desa Bagan Asam, Kec. Toba, Kabupaten Sanggau
        </p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
          <iframe
            title="Lokasi Rumah Rakit 01"
            data-testid="rakit-map"
            src="https://www.google.com/maps?q=-0.4609776663888678,110.00914847859742&z=15&output=embed"
            className="w-full h-72 md:h-full min-h-[280px] rounded-3xl border border-[#E5DECF]"
            loading="lazy"
            allowFullScreen
          />
          <div className="rounded-3xl bg-white border border-[#E5DECF] p-6 flex flex-col justify-center">
            <span className="tag-pill bg-teal-100 text-teal-800 w-fit"><MapPin className="w-3 h-3" /> Lokasi</span>
            <h3 className="mt-3 font-display font-bold text-xl text-stone-900">Rumah Rakit 01</h3>
            <p className="mt-1 text-sm text-stone-600 leading-relaxed">
              Dusun/Desa Bagan Asam, Kec. Toba, Kabupaten Sanggau
            </p>
            <p className="mt-3 text-sm text-stone-600 leading-relaxed">
              Titik kumpul & akses speedboat/sampan dari lokasi ini — admin akan memandu lewat WhatsApp setelah booking.
            </p>
            <a
              data-testid="rakit-maps-link"
              href="https://www.google.com/maps?q=-0.4609776663888678,110.00914847859742"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex w-fit items-center gap-2 px-5 py-2.5 rounded-full bg-teal-700 text-white text-sm font-bold hover:bg-teal-800 transition"
            >
              <MapPin className="w-4 h-4" /> Buka di Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* Paket & Sewa */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-stone-900">Paket & Sewa</h2>
        <p className="text-stone-600 mt-1">Pilih paket rumah rakit, speedboat, atau sampan kato — booking lewat form di bawah.</p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PACKAGES.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className={`rounded-3xl border p-6 ${p.highlight ? "bg-gradient-to-br from-teal-700 to-cyan-900 text-white border-teal-700" : "bg-white border-[#E5DECF]"}`}
              data-testid={`rakit-package-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <p.icon className={`w-7 h-7 ${p.highlight ? "text-teal-300" : "text-teal-700"}`} />
                {p.discount && (
                  <span data-testid={`rakit-package-discount-${p.id}`} className={`tag-pill ${p.highlight ? "bg-teal-400 text-teal-950" : "bg-emerald-100 text-emerald-800"}`}>Hemat 10%</span>
                )}
              </div>
              <h3 className="mt-3 font-display font-bold text-xl leading-tight">{p.name}</h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className={`font-display font-black text-3xl ${p.highlight ? "text-teal-300" : "text-teal-700"}`}>{p.price}</span>
                {p.per && <span className={`text-sm ${p.highlight ? "text-teal-100/80" : "text-stone-500"}`}>{p.per}</span>}
              </div>
              <p className={`mt-2 text-sm leading-relaxed ${p.highlight ? "text-teal-50/90" : "text-stone-600"}`}>{p.desc}</p>
              <a
                href="#booking-rakit"
                onClick={() => setForm((f) => ({ ...f, package: p.id }))}
                data-testid={`rakit-package-book-btn-${p.id}`}
                className={`mt-5 inline-block w-full text-center px-5 py-3 rounded-full font-bold transition ${p.highlight ? "bg-teal-400 text-teal-950 hover:bg-teal-300" : "bg-teal-700 text-white hover:bg-teal-800"}`}
              >
                Booking Paket Ini
              </a>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Kalkulator Estimasi Biaya */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="rakit-calc-section">
        <div className="rounded-3xl bg-white border border-[#E5DECF] p-8">
          <div className="flex items-center gap-2">
            <Calculator className="w-6 h-6 text-teal-700" />
            <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight text-stone-900">Kalkulator Estimasi Biaya</h2>
          </div>
          <p className="text-sm text-stone-600 mt-1">Hitung total booking-mu — paket rumah rakit otomatis <b className="text-emerald-700">diskon 10%</b>.</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label>Paket Rumah Rakit</Label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {[["rakit_12", "12 Jam — Rp 450rb"], ["rakit_24", "24 Jam — Rp 900rb"]].map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      data-testid={`rakit-calc-paket-${id}`}
                      onClick={() => setCalc({ ...calc, paket: id })}
                      className={`px-4 py-3 rounded-2xl text-sm font-bold border transition ${calc.paket === id ? "bg-teal-700 text-white border-teal-700" : "bg-white text-stone-700 border-[#E5DECF] hover:border-teal-600"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Tambah SpeedBoat 15PK (opsional)</Label>
                <Select value={calc.speedboat} onValueChange={(v) => setCalc({ ...calc, speedboat: v })}>
                  <SelectTrigger data-testid="rakit-calc-speedboat" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak sewa</SelectItem>
                    <SelectItem value="speedboat_12">12 Jam — Rp 350.000</SelectItem>
                    <SelectItem value="speedboat_24">24 Jam — Rp 500.000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tambah Sampan/Kato (opsional)</Label>
                <Select value={calc.sampan} onValueChange={(v) => setCalc({ ...calc, sampan: v })}>
                  <SelectTrigger data-testid="rakit-calc-sampan" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak sewa</SelectItem>
                    <SelectItem value="sampan_12">12 Jam — Rp 100.000</SelectItem>
                    <SelectItem value="sampan_24">24 Jam — Rp 150.000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Perkiraan BBM (liter)</Label>
                <Input data-testid="rakit-calc-bbm" type="number" min="0" value={calc.bbm} onChange={(e) => setCalc({ ...calc, bbm: e.target.value })} className="mt-1" placeholder="0 — Rp 15.000/liter" />
              </div>
            </div>
            <div className="rounded-3xl bg-teal-50 border border-teal-200 p-6 flex flex-col">
              <h3 className="font-display font-bold text-lg text-teal-900">Rincian Estimasi</h3>
              <div className="mt-3 space-y-2 text-sm text-stone-700 flex-1">
                <div className="flex justify-between"><span>Paket {calc.paket === "rakit_12" ? "12 Jam" : "24 Jam"}</span><b>{rp(calcPaket)}</b></div>
                <div className="flex justify-between text-emerald-700" data-testid="rakit-calc-discount"><span>Diskon paket 10%</span><b>-{rp(calcDiscount)}</b></div>
                {calc.speedboat !== "none" && (
                  <div className="flex justify-between"><span>SpeedBoat {calc.speedboat === "speedboat_12" ? "12" : "24"} Jam</span><b>{rp(PRICE_NUM[calc.speedboat])}</b></div>
                )}
                {calc.sampan !== "none" && (
                  <div className="flex justify-between"><span>Sampan/Kato {calc.sampan === "sampan_12" ? "12" : "24"} Jam</span><b>{rp(PRICE_NUM[calc.sampan])}</b></div>
                )}
                {calcBbm > 0 && (
                  <div className="flex justify-between"><span>BBM {calc.bbm} liter</span><b>{rp(calcBbm)}</b></div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-teal-200 flex items-baseline justify-between">
                <span className="font-bold text-teal-900">Total Estimasi</span>
                <span data-testid="rakit-calc-total" className="font-display font-black text-3xl text-teal-700">{rp(calcTotal)}</span>
              </div>
              <Button data-testid="rakit-calc-book-btn" onClick={applyCalc} className="mt-4 rounded-full bg-teal-700 hover:bg-teal-800 font-bold py-6">
                Booking dengan Estimasi Ini
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Form Booking */}
      <section id="booking-rakit" className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="rounded-3xl bg-gradient-to-br from-teal-700 to-teal-950 text-white p-8 md:p-10">
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">Form Booking Rumah Rakit</h2>
          <p className="text-teal-100/90 mt-2 max-w-xl">Isi data di bawah — admin akan hubungi via WhatsApp untuk konfirmasi jadwal & titik kumpul.</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
            <div>
              <Label className="text-teal-100">Nama Lengkap</Label>
              <Input data-testid="rakit-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white text-stone-900" placeholder="Nama kamu" />
            </div>
            <div>
              <Label className="text-teal-100">Nomor HP / WhatsApp</Label>
              <Input data-testid="rakit-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-white text-stone-900" placeholder="0812xxxxxxxx" />
            </div>
            <div>
              <Label className="text-teal-100">Paket / Sewa</Label>
              <Select value={form.package} onValueChange={(v) => setForm({ ...form, package: v })}>
                <SelectTrigger data-testid="rakit-form-package" className="mt-1 bg-white text-stone-900"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PACKAGES.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} — {p.price}{p.per || ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-teal-100">Rencana Tanggal</Label>
              <Input data-testid="rakit-form-date" type="date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} className="mt-1 bg-white text-stone-900" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-teal-100">Catatan (opsional)</Label>
              <Textarea data-testid="rakit-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-white text-stone-900" rows={2} placeholder="Mis. jumlah orang, mau tambah speedboat, bawa alat pancing sendiri" />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              data-testid="rakit-form-submit-btn"
              onClick={submit}
              disabled={loading}
              className="rounded-full bg-teal-400 text-teal-950 hover:bg-teal-300 font-bold px-8 py-6 text-base"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mengirim...</> : "Kirim Booking"}
            </Button>
            {sent && (
              <a
                data-testid="rakit-wa-btn"
                onClick={() => trackWaClick("rumahrakit")}
                href={`https://wa.me/${WA_ADMIN}?text=${encodeURIComponent(waMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-white font-bold hover:bg-emerald-400 transition"
              >
                <MessageCircle className="w-4 h-4" /> Lanjut ke WA Admin
              </a>
            )}
          </div>
        </div>
      </section>

      <footer className="relative border-t border-[#E5DECF] py-6 text-center text-xs text-stone-500 font-mono uppercase tracking-widest">
        <a data-testid="footer-wa-link" onClick={() => trackWaClick("rumahrakit")} href={`https://wa.me/${WA_ADMIN}?text=${encodeURIComponent("Halo Admin FAS Group, saya mau tanya RUMAH RAKIT...")}`} target="_blank" rel="noopener noreferrer" className="mb-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-700 text-white text-sm font-bold normal-case tracking-normal hover:bg-emerald-800 transition">
          <MessageCircle className="w-4 h-4" /> Admin FAS Group Corp.
        </a>
        <div>RUMAH RAKIT by FAS Group · Wisata Mancing + Healing di Rumah Terapung</div>
        <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/kpyu0nvc_ChatGPT%20Image%2026%20Agu%202026%2C%2012.19.43.png" alt="FAS Group Corp" data-testid="footer-fasgroup-logo" className="mx-auto mt-4 h-14 w-auto object-contain rounded-xl bg-white p-1.5 border border-[#E5DECF] md:absolute md:right-6 md:bottom-4 md:m-0 md:h-16" />
      </footer>

      <Lightbox
        images={lightbox.images}
        index={lightbox.index}
        onClose={() => setLightbox((l) => ({ ...l, index: null }))}
        onNavigate={(i) => setLightbox((l) => ({ ...l, index: i }))}
      />
    </div>
  );
}
